import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarPlus, XCircle, Clock3, List, CalendarDays, StickyNote } from "lucide-react";
import { bookingRequest } from "./bookingApi";
import AppointmentsCalendar, { addDays, startOfWeek, toYMD } from "./AppointmentsCalendar";

const ALL_STATUSES = ["scheduled", "confirmed", "in_progress", "completed", "cancelled", "no_show"];
const RELEASING_STATUSES = ["cancelled", "no_show"];
// A visit that has started or happened can't be cancelled, marked no-show
// or rescheduled - the backend 400s (it would erase the visit's record).
const VISIT_STARTED_STATUSES = ["in_progress", "completed"];
// Mirrors backend booking_store.ALLOWED_STATUS_TRANSITIONS: status changes
// only move forward (the backend 400s anything else). Cancelled / no-show
// are reached through the cancel route, and only before the visit starts.
const NEXT_STATUSES = {
  scheduled: ["confirmed", "in_progress", "completed", "cancelled", "no_show"],
  confirmed: ["in_progress", "completed", "cancelled", "no_show"],
  in_progress: ["completed"],
  completed: [],
};
const statusOptions = (current) => [current, ...(NEXT_STATUSES[current] || [])];

const statusLabel = (status) => status.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");

const AppointmentsTab = ({ basePath, token }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);

  const [filterProvider, setFilterProvider] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  // "list" is the filterable table (existing behavior, unchanged); the
  // week grid reuses the same fetch but drives its own date range instead
  // of the From/To inputs, which are hidden while it's active.
  const [view, setView] = useState("list");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));

  // Non-null while a booking dialog is open. `appointment: null` means
  // "New Appointment"; a real appointment means the same dialog is
  // reused to reschedule it (existing patient/service fixed, new
  // provider/day/slot chosen) instead of duplicating this UI twice.
  const [bookingDialog, setBookingDialog] = useState(null);
  const [selPatientId, setSelPatientId] = useState("");
  const [selServiceId, setSelServiceId] = useState("");
  const [selProviderId, setSelProviderId] = useState("");
  const [selDay, setSelDay] = useState("");
  const [selNotes, setSelNotes] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);

  const patientsById = useMemo(() => Object.fromEntries(patients.map((p) => [p.id, p])), [patients]);
  const providersById = useMemo(() => Object.fromEntries(providers.map((p) => [p.id, p])), [providers]);

  const loadReferenceData = useCallback(async () => {
    // allSettled: each lookup list is independently useful (e.g. provider
    // names and the provider filter still work if /patients fails), so one
    // failing request must not leave all three empty.
    const results = await Promise.allSettled([
      bookingRequest(basePath, token, "GET", "/patients", { params: { limit: 200 } }),
      bookingRequest(basePath, token, "GET", "/services", { params: { active_only: "true" } }),
      bookingRequest(basePath, token, "GET", "/providers", { params: { active_only: "true" } }),
    ]);
    const targets = [
      ["patients", setPatients],
      ["services", setServices],
      ["providers", setProviders],
    ];
    const failed = [];
    results.forEach((result, index) => {
      const [label, setter] = targets[index];
      if (result.status === "fulfilled") setter(result.value?.items || []);
      else failed.push(label);
    });
    if (failed.length) toast.error(`Failed to load ${failed.join("/")}`);
  }, [basePath, token]);

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const [rangeFrom, rangeTo] =
        view === "calendar"
          ? [toYMD(weekStart), toYMD(addDays(weekStart, 6))]
          : [filterFrom, filterTo];
      const data = await bookingRequest(basePath, token, "GET", "/appointments", {
        params: {
          provider_id: filterProvider || undefined,
          status: view === "calendar" ? undefined : filterStatus || undefined,
          // Local-day boundaries, sent as UTC instants. Appending "Z" to the
          // local date treated it as UTC midnight, so a US clinic's evening
          // appointments fell outside the week view and the "To" filter.
          starts_after: rangeFrom ? new Date(`${rangeFrom}T00:00:00`).toISOString() : undefined,
          starts_before: rangeTo ? new Date(`${rangeTo}T23:59:59.999`).toISOString() : undefined,
          limit: 200,
        },
      });
      setAppointments(data.items || []);
    } catch (err) {
      toast.error(err.message || "Failed to load appointments");
    } finally {
      setLoading(false);
    }
  }, [basePath, token, filterProvider, filterStatus, filterFrom, filterTo, view, weekStart]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const qualifiedProviders = useMemo(() => {
    const service = services.find((s) => s.id === selServiceId);
    if (!service || !service.provider_ids || service.provider_ids.length === 0) return providers;
    return providers.filter((p) => service.provider_ids.includes(p.id));
  }, [services, providers, selServiceId]);

  const openNewAppointment = () => {
    setBookingDialog({ appointment: null });
    setSelPatientId("");
    setSelServiceId("");
    setSelProviderId("");
    setSelDay("");
    setSelNotes("");
    setSlots([]);
  };

  const openReschedule = (appointment) => {
    setBookingDialog({ appointment });
    setSelPatientId(appointment.patient_id);
    setSelServiceId(appointment.service_id);
    setSelProviderId(appointment.provider_id);
    setSelDay("");
    setSelNotes("");
    setSlots([]);
  };

  const fetchSlots = useCallback(async () => {
    if (!selProviderId || !selServiceId || !selDay) {
      setSlots([]);
      return;
    }
    setLoadingSlots(true);
    try {
      const data = await bookingRequest(basePath, token, "GET", "/availability", {
        params: {
          provider_id: selProviderId,
          service_id: selServiceId,
          day: selDay,
          // Rescheduling: the appointment's own currently-claimed slots
          // must not count as "taken", or a move to any nearby/overlapping
          // time (which the backend explicitly supports - see
          // reschedule_appointment) never appears as a pickable option.
          exclude_appointment_id: bookingDialog?.appointment?.id,
        },
      });
      setSlots(data.available_starts || []);
    } catch (err) {
      toast.error(err.message || "Failed to load availability");
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [basePath, token, selProviderId, selServiceId, selDay, bookingDialog]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const bookSlot = async (startIso) => {
    setBooking(true);
    try {
      if (bookingDialog.appointment) {
        await bookingRequest(
          basePath,
          token,
          "POST",
          `/appointments/${bookingDialog.appointment.id}/reschedule`,
          { body: { starts_at: startIso, provider_id: selProviderId } }
        );
        toast.success("Appointment rescheduled");
      } else {
        await bookingRequest(basePath, token, "POST", "/appointments", {
          body: { patient_id: selPatientId, provider_id: selProviderId, service_id: selServiceId, starts_at: startIso, notes: selNotes },
        });
        toast.success("Appointment booked");
      }
      setBookingDialog(null);
      loadAppointments();
    } catch (err) {
      if (err.status === 409) {
        // Lost a race to another booking - not a failure, just stale
        // availability. Show it plainly and refresh the slot list rather
        // than a generic error.
        toast.error("That slot was just taken - pick another time");
        fetchSlots();
      } else {
        toast.error(err.message || "Could not book that slot");
      }
    } finally {
      setBooking(false);
    }
  };

  const cancelAppointment = async (appointment) => {
    if (!window.confirm("Cancel this appointment?")) return;
    try {
      await bookingRequest(basePath, token, "POST", `/appointments/${appointment.id}/cancel`, {
        params: { status: "cancelled" },
      });
      toast.success("Appointment cancelled");
      loadAppointments();
    } catch (err) {
      toast.error(err.message || "Could not cancel appointment");
    }
  };

  const changeStatus = async (appointment, newStatus) => {
    try {
      if (RELEASING_STATUSES.includes(newStatus)) {
        await bookingRequest(basePath, token, "POST", `/appointments/${appointment.id}/cancel`, {
          params: { status: newStatus },
        });
      } else {
        await bookingRequest(basePath, token, "POST", `/appointments/${appointment.id}/status`, {
          params: { status: newStatus },
        });
      }
      toast.success("Status updated");
      loadAppointments();
    } catch (err) {
      toast.error(err.message || "Could not update status");
    }
  };

  return (
    <Card className="glass-panel border-white/10" data-testid="appointments-tab">
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <Label className="text-white/60 text-xs">Provider</Label>
            <Select value={filterProvider || "all"} onValueChange={(v) => setFilterProvider(v === "all" ? "" : v)}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All providers</SelectItem>
                {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {view === "list" && (
            <div>
              <Label className="text-white/60 text-xs">Status</Label>
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {view === "list" && (
            <div>
              <Label className="text-white/60 text-xs">From</Label>
              <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="bg-white/5 border-white/10 text-white" />
            </div>
          )}
          {view === "list" && (
            <div>
              <Label className="text-white/60 text-xs">To</Label>
              <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="bg-white/5 border-white/10 text-white" />
            </div>
          )}
          <div className="flex items-center gap-1">
            <Button
              type="button"
              className={`ghost-btn text-xs ${view === "list" ? "" : "opacity-50"}`}
              style={view === "list" ? { borderColor: "rgba(212,166,42,0.5)" } : undefined}
              onClick={() => setView("list")}
              data-testid="appointments-view-list"
            >
              <List size={14} /> List
            </Button>
            <Button
              type="button"
              className={`ghost-btn text-xs ${view === "calendar" ? "" : "opacity-50"}`}
              style={view === "calendar" ? { borderColor: "rgba(212,166,42,0.5)" } : undefined}
              onClick={() => setView("calendar")}
              data-testid="appointments-view-calendar"
            >
              <CalendarDays size={14} /> Calendar
            </Button>
          </div>
          <Button className="primary-btn ml-auto" onClick={openNewAppointment} data-testid="new-appointment-btn">
            <CalendarPlus size={16} />
            New Appointment
          </Button>
        </div>

        {view === "calendar" ? (
          loading && appointments.length === 0 ? (
            <p className="text-white/60 text-sm">Loading appointments...</p>
          ) : (
            <AppointmentsCalendar
              weekStart={weekStart}
              onWeekChange={setWeekStart}
              appointments={appointments}
              patientsById={patientsById}
              providersById={providersById}
              onSelect={openReschedule}
            />
          )
        ) : loading ? (
          <p className="text-white/60 text-sm">Loading appointments...</p>
        ) : appointments.length === 0 ? (
          <p className="text-white/60 text-sm py-8 text-center">No appointments match these filters.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-white/60">Patient</TableHead>
                <TableHead className="text-white/60">Service</TableHead>
                <TableHead className="text-white/60">Provider</TableHead>
                <TableHead className="text-white/60">When</TableHead>
                <TableHead className="text-white/60">Status</TableHead>
                <TableHead className="text-white/60 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointments.map((appt) => {
                const patient = patientsById[appt.patient_id];
                const provider = providersById[appt.provider_id];
                return (
                  <TableRow key={appt.id} data-testid={`appointment-row-${appt.id}`}>
                    <TableCell className="text-white/90">
                      <span className="inline-flex items-center gap-1.5">
                        {patient ? `${patient.first_name} ${patient.last_name}` : appt.patient_id}
                        {appt.notes && (
                          <StickyNote size={13} className="text-white/40 shrink-0" title={appt.notes} />
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-white/70">{appt.service_name}</TableCell>
                    <TableCell className="text-white/70">{provider ? provider.full_name : appt.provider_id}</TableCell>
                    <TableCell className="text-white/70">{new Date(appt.starts_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <Select
                        value={appt.status}
                        onValueChange={(v) => changeStatus(appt, v)}
                        disabled={RELEASING_STATUSES.includes(appt.status) || appt.status === "completed"}
                      >
                        <SelectTrigger className="bg-white/5 border-white/10 text-white w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {/* A cancelled/no-show appointment already released its calendar
                              slot (see backend's cancel_appointment) - the backend now
                              rejects reactivating it via this status change, so the picker
                              is locked here rather than letting staff pick an option that
                              always 400s. */}
                          {statusOptions(appt.status).map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {!RELEASING_STATUSES.includes(appt.status) && !VISIT_STARTED_STATUSES.includes(appt.status) && (
                        <>
                          <Button className="ghost-btn text-xs" onClick={() => openReschedule(appt)} data-testid={`reschedule-appointment-${appt.id}`}>
                            <Clock3 size={14} /> Reschedule
                          </Button>
                          <Button className="ghost-btn text-xs" onClick={() => cancelAppointment(appt)} data-testid={`cancel-appointment-${appt.id}`}>
                            <XCircle size={14} /> Cancel
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={!!bookingDialog} onOpenChange={(open) => !open && setBookingDialog(null)}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{bookingDialog?.appointment ? "Reschedule Appointment" : "New Appointment"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!bookingDialog?.appointment && (
              <div>
                <Label className="text-white/70">Patient</Label>
                <Select value={selPatientId} onValueChange={setSelPatientId}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select patient" /></SelectTrigger>
                  <SelectContent>
                    {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!bookingDialog?.appointment && (
              <div>
                <Label className="text-white/70">Service</Label>
                <Select value={selServiceId} onValueChange={(v) => { setSelServiceId(v); setSelProviderId(""); }}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent>
                    {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.duration_minutes} min)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-white/70">Provider</Label>
              <Select value={selProviderId} onValueChange={setSelProviderId}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select provider" /></SelectTrigger>
                <SelectContent>
                  {qualifiedProviders.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70">Day</Label>
              <Input type="date" value={selDay} onChange={(e) => setSelDay(e.target.value)} className="bg-white/5 border-white/10 text-white" />
            </div>

            {!bookingDialog?.appointment ? (
              <div>
                <Label className="text-white/70">Notes</Label>
                <Textarea
                  value={selNotes}
                  onChange={(e) => setSelNotes(e.target.value)}
                  placeholder="Optional - e.g. requested provider preference, referral source"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="appointment-notes-input"
                />
              </div>
            ) : (
              bookingDialog.appointment.notes && (
                <div>
                  <Label className="text-white/70">Notes</Label>
                  <p className="text-white/50 text-sm bg-white/5 border border-white/10 rounded-md px-3 py-2">
                    {bookingDialog.appointment.notes}
                  </p>
                </div>
              )
            )}

            <div>
              <Label className="text-white/70 mb-2 block">Available times</Label>
              {loadingSlots ? (
                <p className="text-white/50 text-sm">Loading availability...</p>
              ) : !selProviderId || !selServiceId || !selDay ? (
                <p className="text-white/50 text-sm">Pick a service, provider, and day to see open slots.</p>
              ) : slots.length === 0 ? (
                <p className="text-white/50 text-sm">No open slots that day.</p>
              ) : (
                <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                  {slots.map((iso) => (
                    <Button
                      key={iso}
                      type="button"
                      className="ghost-btn text-xs"
                      disabled={booking}
                      onClick={() => bookSlot(iso)}
                      data-testid={`slot-${iso}`}
                    >
                      {new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" className="ghost-btn" onClick={() => setBookingDialog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AppointmentsTab;
